import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchConvertTrade, fetchConvertTrades, formatAedAmount } from "../../../api/services/fiatService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { exportConvertHistoryBrandedExcel } from "../../../utils/exportConvertHistoryExcel";
import { formatQuoteAmount, convertDeepLink, quoteFeeLabel } from "./convertCatalog";
import "../DepositFiatPage/depositfiat.css";
import "../WithdrawFiatPage/withdrawFiat.css";
import "../WithdrawPage/withdrawal.css";

const PAGE_SIZE = 20;

const TIME_FILTER_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All status" },
  { value: "BUY", label: "Buy" },
  { value: "SELL", label: "Sell" },
  { value: "EXECUTED", label: "Executed" },
  { value: "FAILED", label: "Failed" },
];

const STATUS_LABEL = {
  EXECUTED: "Executed",
  QUOTED: "Quoted",
  EXPIRED: "Expired",
  FAILED: "Failed",
};

function statusTone(status) {
  if (status === "EXECUTED") return "success";
  if (status === "QUOTED") return "pending";
  if (status === "EXPIRED") return "neutral";
  if (status === "FAILED") return "danger";
  return "neutral";
}

function formatHistDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function qtyPlaces(code) {
  return code === "AED" ? 2 : 6;
}

function mapTrade(item) {
  const status = String(item?.status || "").toUpperCase();
  const side = String(item?.side || "").toUpperCase();
  const spendCode = side === "BUY" ? item.quote_asset : item.base_asset;
  const receiveCode = side === "BUY" ? item.base_asset : item.quote_asset;
  return {
    id: String(item.id || item.quote_id || ""),
    createdAt: item.executed_at || item.created_at || null,
    dateLabel: formatHistDate(item.executed_at || item.created_at),
    createdLabel: formatHistDate(item.created_at),
    executedLabel: formatHistDate(item.executed_at),
    side,
    sideLabel: side === "SELL" ? "Sell" : "Buy",
    spend: item.you_spend || (side === "BUY" ? item.amount_aed : item.amount_crypto) || "0",
    spendCode: spendCode || "AED",
    receive: item.you_receive || (side === "BUY" ? item.amount_crypto : item.amount_aed) || "0",
    receiveCode: receiveCode || "USDT",
    fee: item.fee_aed || "0",
    feeType: String(item.fee_type || "").toUpperCase(),
    feeBps: item.fee_bps,
    rate: item.user_rate || item.cmc_rate || "",
    wallet: "Spot",
    status,
    statusLabel: STATUS_LABEL[status] || status || "—",
    statusTone: statusTone(status),
    asset: String(item.base_asset || "USDT").toUpperCase(),
    href: convertDeepLink(side === "SELL" ? "sell" : "buy", item.base_asset || "USDT"),
  };
}

function spendLabel(row) {
  return `${formatQuoteAmount(row.spend, qtyPlaces(row.spendCode))} ${row.spendCode}`;
}

function receiveLabel(row) {
  return `${formatQuoteAmount(row.receive, qtyPlaces(row.receiveCode))} ${row.receiveCode}`;
}

function feeLabel(row) {
  return quoteFeeLabel({ fee_aed: row.fee, fee_type: row.feeType, fee_bps: row.feeBps }, formatAedAmount);
}

function rowWithinTimeFilter(row, timeFilter) {
  if (!timeFilter || timeFilter === "all") return true;
  if (!row.createdAt) return true;
  const d = new Date(row.createdAt);
  if (Number.isNaN(d.getTime())) return true;
  const days = { "7d": 7, "30d": 30, "90d": 90 }[timeFilter];
  if (!days) return true;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - days);
  return d >= cutoff;
}

function DfFilterDropdown({ id, label, value, options, openKey, menuKey, onOpenToggle, onChange }) {
  const open = openKey === menuKey;
  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className={`df-dd${open ? " is-open" : ""}`} data-df-dropdown>
      <span className="df-dd-label" id={`${id}-lbl`}>
        {label}
      </span>
      <button
        type="button"
        id={id}
        className={`df-dd-trigger${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-lbl`}
        onClick={(e) => {
          e.stopPropagation();
          onOpenToggle(open ? null : menuKey);
        }}
      >
        <span className="df-dd-value">{selected.label}</span>
        <svg className="df-dd-chevron" width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
          <path fill="currentColor" d="M1.4.6 6 5.2 10.6.6 12 2 6 8 0 2z" />
        </svg>
      </button>
      {open ? (
        <ul className="df-dd-menu" role="listbox" aria-labelledby={`${id}-lbl`}>
          {options.map((opt) => (
            <li key={opt.value} role="option" aria-selected={opt.value === value}>
              <button
                type="button"
                className={`df-dd-option${opt.value === value ? " is-selected" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  onOpenToggle(null);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

async function copyText(text) {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function ConvertDetailModal({ open, row, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !row) return null;

  const stepperOutcome = row.statusTone === "success" ? "success" : row.statusTone === "danger" ? "danger" : "pending";
  const fields = [
    { label: "Status", value: row.statusLabel },
    { label: "Date", value: row.dateLabel },
    { label: "Side", value: row.sideLabel },
    { label: "You spend", value: spendLabel(row) },
    { label: "You receive", value: receiveLabel(row) },
    { label: "Fee", value: feeLabel(row) },
    { label: "Rate", value: row.rate ? `1 ${row.asset} ≈ ${row.rate} AED` : "" },
    { label: "Wallet", value: row.wallet },
    { label: "Reference", value: row.id, copy: true },
  ].filter((f) => f.value && f.value !== "—");

  return (
    <div className="withdraw_confirm_overlay withdraw_hist_detail_overlay" role="presentation" onClick={onClose}>
      <div
        className="withdraw_hist_detail_modal wf-fiat-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bsp-convert-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className="withdraw_hist_detail_head">
          <h2 id="bsp-convert-detail-title" className="withdraw_hist_detail_title">
            Convert details
          </h2>
        </div>
        <div className="withdraw_hist_detail_body">
          <div className="withdraw_hist_stepper" aria-label="Convert progress" data-outcome={stepperOutcome}>
            <div className="withdraw_hist_step">
              <div className="withdraw_hist_step_track" aria-hidden="true">
                <span className="withdraw_hist_step_dot" />
                <span className="withdraw_hist_step_line" />
              </div>
              <div className="withdraw_hist_step_text">
                <div className="withdraw_hist_step_title">Convert requested</div>
                <div className="withdraw_hist_step_time">{row.createdLabel || row.dateLabel}</div>
              </div>
            </div>
            <div className="withdraw_hist_step">
              <div className="withdraw_hist_step_track" aria-hidden="true">
                <span className="withdraw_hist_step_dot" />
              </div>
              <div className="withdraw_hist_step_text">
                <div className="withdraw_hist_step_title is-emphasized">{row.statusLabel}</div>
                <div className="withdraw_hist_step_time">{row.executedLabel || row.dateLabel}</div>
              </div>
            </div>
          </div>
          <div className="withdraw_hist_rows">
            {fields.map((f) => (
              <div key={f.label} className="withdraw_hist_row">
                <span className="withdraw_hist_row_label">{f.label}</span>
                <span className="withdraw_hist_row_value">
                  {f.copy ? (
                    <>
                      {f.value}
                      <button
                        type="button"
                        className="df-deposit-history-copy-btn"
                        onClick={async () => {
                          const ok = await copyText(f.value);
                          if (ok) alertSuccessMessage("Copied");
                        }}
                        aria-label={`Copy ${f.label}`}
                      >
                        <i className="ri-file-copy-line" aria-hidden />
                      </button>
                    </>
                  ) : (
                    f.value
                  )}
                </span>
              </div>
            ))}
          </div>
          <div className="withdraw_hist_help_links wf-fiat-detail-help">
            <Link to={row.href} className="withdraw_hist_gold_link" onClick={onClose}>
              {row.side === "SELL" ? `Sell ${row.asset} again` : `Buy ${row.asset} again`}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConvertHistory({ loggedIn, reloadToken = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [timeFilter, setTimeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadPage = useCallback(
    async (nextPage, replace) => {
      if (!loggedIn) {
        setRows([]);
        setHasMore(false);
        setLoading(false);
        return;
      }
      if (replace) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      const side = statusFilter === "BUY" || statusFilter === "SELL" ? statusFilter : undefined;
      const status =
        statusFilter === "EXECUTED" || statusFilter === "FAILED" ? statusFilter : "EXECUTED";
      const res = await fetchConvertTrades({
        page: nextPage,
        limit: PAGE_SIZE,
        status: statusFilter === "FAILED" ? "FAILED" : status,
        side,
      });
      if (!res.ok) {
        if (replace) {
          setRows([]);
          setError(res.error?.message || "Could not load convert history.");
        }
        setHasMore(false);
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      const mapped = (res.items || []).map(mapTrade);
      setRows((prev) => (replace ? mapped : [...prev, ...mapped]));
      setPage(nextPage);
      setHasMore(!!res.meta?.has_more);
      setLoading(false);
      setLoadingMore(false);
    },
    [loggedIn, statusFilter]
  );

  useEffect(() => {
    loadPage(1, true);
  }, [loadPage, reloadToken]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-df-dropdown]")) setOpenDropdown(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [openDropdown]);

  const filteredRows = useMemo(
    () => rows.filter((row) => rowWithinTimeFilter(row, timeFilter)),
    [rows, timeFilter]
  );

  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    if (!row?.id) return;
    const res = await fetchConvertTrade(row.id);
    if (res.ok && res.trade) {
      setDetailRow(mapTrade(res.trade));
    }
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailRow(null);
  }, []);

  const handleExportExcel = useCallback(async () => {
    if (!filteredRows.length) {
      alertErrorMessage("No convert history to export.");
      return;
    }
    try {
      const timeLabel =
        TIME_FILTER_OPTIONS.find((o) => o.value === timeFilter)?.label || "All time";
      const statusLabel =
        STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label || "All status";
      const exportRows = filteredRows.map((row) => ({
        ...row,
        spendDisplay: spendLabel(row),
        receiveDisplay: receiveLabel(row),
        feeDisplay: feeLabel(row),
      }));
      await exportConvertHistoryBrandedExcel(exportRows, { timeLabel, statusLabel });
      alertSuccessMessage("Convert history exported.");
    } catch {
      alertErrorMessage("Could not export convert history.");
    }
  }, [filteredRows, timeFilter, statusFilter]);

  const bindRowOpen = (row) => ({
    tabIndex: 0,
    role: "button",
    onClick: () => openDetail(row),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openDetail(row);
      }
    },
  });

  if (!loggedIn) return null;

  return (
    <>
      <section className="df-deposit-history-section" aria-labelledby="bsp-convert-history-title">
        <div className="container">
          <div className="df-deposit-history-top">
            <h2 id="bsp-convert-history-title" className="df-deposit-history-title">
              Convert history
            </h2>
            <div className="df-deposit-history-filters" role="search" aria-label="Filter convert history">
              <DfFilterDropdown
                id="bsp-hist-time"
                label="Time"
                value={timeFilter}
                options={TIME_FILTER_OPTIONS}
                menuKey="time"
                openKey={openDropdown}
                onOpenToggle={setOpenDropdown}
                onChange={setTimeFilter}
              />
              <DfFilterDropdown
                id="bsp-hist-status"
                label="Status"
                value={statusFilter}
                options={STATUS_FILTER_OPTIONS}
                menuKey="status"
                openKey={openDropdown}
                onOpenToggle={setOpenDropdown}
                onChange={setStatusFilter}
              />
              <button
                type="button"
                className="df-deposit-history-export-btn"
                onClick={handleExportExcel}
                disabled={loading || filteredRows.length === 0}
              >
                Export Excel
              </button>
            </div>
          </div>

          <div className="df-deposit-history-card">
            <div className="df-deposit-history-table-wrap" role="region" aria-label="Convert history table">
              {error ? (
                <div className="df-deposit-history-status" role="status">
                  <p>{error}</p>
                  <button type="button" className="df-deposit-history-export-btn" onClick={() => loadPage(1, true)}>
                    Try again
                  </button>
                </div>
              ) : null}
              {loading && rows.length === 0 && !error ? (
                <div className="df-sk-table-wrap" aria-label="Loading convert history">
                  {Array.from({ length: 6 }, (_, i) => (
                    <div className="df-sk-row" key={i}>
                      <div className="df-sk-coin">
                        <span className="df-sk-circle df-sk-glass" />
                        <div className="df-sk-coin-txt">
                          <span className="df-sk-glass df-sk-bone--xs" />
                          <span className="df-sk-glass df-sk-bone--xxs" />
                        </div>
                      </div>
                      <span className="df-sk-glass df-sk-bone--cell" />
                      <span className="df-sk-glass df-sk-bone--cell" />
                      <span className="df-sk-glass df-sk-bone--cell df-sk-w50" />
                    </div>
                  ))}
                </div>
              ) : null}
              {!loading && filteredRows.length === 0 && !error ? (
                <div className="df-deposit-history-empty" aria-live="polite">
                  <img src="/images/no-data.svg" alt="" className="df-deposit-history-empty-img" width={120} height={120} />
                  <p className="df-deposit-history-empty-copy">No converts yet. Confirm a convert to add it here.</p>
                </div>
              ) : null}
              {filteredRows.length > 0 ? (
                <>
                  <div className="df-deposit-history-desktop df-deposit-history-desktop--fiat df-deposit-history-desktop--withdraw">
                    <div className="df-deposit-history-head-wrap">
                      <div className="df-deposit-history-head" role="row">
                        {["Date", "Amount", "Fee", "Wallet", "You receive", "Status", "Action"].map((label) => (
                          <div key={label} className="df-deposit-history-head-cell" role="columnheader">
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="df-deposit-history-body-wrap" aria-label="Recent converts">
                      {filteredRows.map((row) => (
                        <div key={row.id} className="df-deposit-history-row" role="row" {...bindRowOpen(row)}>
                          <div className="df-deposit-history-row-cell" title={row.dateLabel}>
                            {row.dateLabel}
                          </div>
                          <div className="df-deposit-history-row-cell">{spendLabel(row)}</div>
                          <div className="df-deposit-history-row-cell">{feeLabel(row)}</div>
                          <div className="df-deposit-history-row-cell">{row.wallet}</div>
                          <div className="df-deposit-history-row-cell">{receiveLabel(row)}</div>
                          <div className="df-deposit-history-row-cell">
                            <span className={`df-deposit-history-action-badge is-${row.statusTone}`}>
                              {row.statusLabel}
                            </span>
                          </div>
                          <div className="df-deposit-history-row-cell df-deposit-history-row-cell--action">
                            <button
                              type="button"
                              className="wf-history-review-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(row);
                              }}
                            >
                              Review
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="df-deposit-history-mobile" aria-label="Recent converts">
                    {filteredRows.map((row) => (
                      <div key={row.id} className="df-deposit-history-mobile-card" {...bindRowOpen(row)}>
                        <div className="df-deposit-history-mobile-top">
                          <span className="df-deposit-history-mobile-date">{row.dateLabel}</span>
                          <span className={`df-deposit-history-action-badge is-${row.statusTone}`}>
                            {row.statusLabel}
                          </span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Amount</span>
                          <span className="df-deposit-history-mobile-value">{spendLabel(row)}</span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Fee</span>
                          <span className="df-deposit-history-mobile-value">{feeLabel(row)}</span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Wallet</span>
                          <span className="df-deposit-history-mobile-value">{row.wallet}</span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">You receive</span>
                          <span className="df-deposit-history-mobile-value">{receiveLabel(row)}</span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Action</span>
                          <span className="df-deposit-history-mobile-value wf-history-mobile-actions">
                            <button
                              type="button"
                              className="wf-history-review-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(row);
                              }}
                            >
                              Review
                            </button>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
              {hasMore && !error ? (
                <div className="df-deposit-history-more">
                  <button
                    type="button"
                    className="df-deposit-history-export-btn"
                    onClick={() => loadPage(page + 1, false)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <ConvertDetailModal open={detailOpen} row={detailRow} onClose={closeDetail} />
    </>
  );
}
